import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Package, Plus, Trash2, Edit3, ArrowDownToLine, ArrowUpFromLine, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, Search, History, Clock,
  ShoppingCart, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useTrackedSearch, trackTab, trackView } from "../activityTracker";

const REQUEST_STATUS_COLORS = {
  Pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Approved: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Issued: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Rejected: "bg-red-500/15 text-red-300 border-red-500/30",
};

const STOCK_UNITS = ["pcs", "pair", "set", "box", "kg", "m", "L"];

export default function Stock() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = !!user?.is_owner;
  const [tab, setTab] = useState("items"); // items | requests | movements
  const switchTab = (t) => { setTab(t); try { trackTab("stock", t); } catch { /* noop */ } };

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [itemDlg, setItemDlg] = useState(null);          // { mode: 'new' | 'edit', item? }
  const [requestDlg, setRequestDlg] = useState(null);     // { item }
  const [receiveDlg, setReceiveDlg] = useState(null);     // { item }
  const [issueDlg, setIssueDlg] = useState(null);         // { req }
  const [adjustDlg, setAdjustDlg] = useState(null);       // { item }
  const [historyItem, setHistoryItem] = useState(null);   // item — opens drawer

  // Filters
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  useTrackedSearch("stock", q);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [it, ca, rq, su] = await Promise.all([
        api.get("/stock/items"),
        api.get("/stock/categories"),
        api.get("/stock/requests?days=90"),
        api.get("/stock/summary"),
      ]);
      setItems(it.data); setCategories(ca.data);
      setRequests(rq.data); setSummary(su.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stock data");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // ---------- handlers ----------
  const saveItem = async (payload, id) => {
    try {
      if (id) {
        await api.patch(`/stock/items/${id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/stock/items", payload);
        toast.success("Item added");
      }
      setItemDlg(null); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? Items with movements will be soft-deleted.`)) return;
    try { await api.delete(`/stock/items/${item.id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const submitRequest = async ({ item_id, qty, reason }) => {
    try {
      await api.post("/stock/requests", { item_id, qty: Number(qty), reason });
      toast.success("Request submitted to admins");
      setRequestDlg(null); switchTab("requests"); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };
  const submitReceive = async ({ item_id, qty, note }) => {
    try {
      await api.post(`/stock/items/${item_id}/receive`, { qty: Number(qty), note });
      toast.success("Stock received");
      setReceiveDlg(null); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Receive failed"); }
  };
  const submitAdjust = async ({ item_id, new_balance, note }) => {
    try {
      await api.post(`/stock/items/${item_id}/adjust`, { new_balance: Number(new_balance), note });
      toast.success("Balance adjusted");
      setAdjustDlg(null); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjust failed"); }
  };
  const approveRequest = async (r) => {
    try { await api.post(`/stock/requests/${r.id}/approve`, { note: "" }); toast.success("Approved"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const rejectRequest = async (r) => {
    const note = window.prompt("Reason for rejection (optional):", "");
    if (note === null) return;
    try { await api.post(`/stock/requests/${r.id}/reject`, { note }); toast.success("Rejected"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };
  const issueRequest = async ({ rid, qty, note }) => {
    try {
      const body = note != null || qty != null ? { qty: qty != null ? Number(qty) : null, note } : {};
      await api.post(`/stock/requests/${rid}/issue`, body);
      toast.success("Issued + stock deducted");
      setIssueDlg(null); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
  };
  const deleteRequest = async (r) => {
    if (!window.confirm(`Delete request "${r.item_name}"?`)) return;
    try { await api.delete(`/stock/requests/${r.id}`); toast.success("Removed"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const openHistory = (item) => {
    setHistoryItem(item);
    try { trackView("stock_item", item.id, item.name, "stock"); } catch { /* noop */ }
  };

  // ---------- filtered slices ----------
  const filteredItems = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items
      .filter((i) => !categoryFilter || i.category_id === categoryFilter)
      .filter((i) => {
        if (!term) return true;
        return (i.name || "").toLowerCase().includes(term)
          || (i.sku || "").toLowerCase().includes(term)
          || (i.category_name || "").toLowerCase().includes(term);
      });
  }, [items, q, categoryFilter]);

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => !statusFilter || r.status === statusFilter);
  }, [requests, statusFilter]);

  // ---------- render ----------
  return (
    <Layout>
      <div className="space-y-5" data-testid="stock-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Package className="w-6 h-6 text-sky-400" /> Stock
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Track items (uniform, safety shoes, bunk beds…) · request → admin approves → balance auto-deducts on issue.
              {!isOwner && (
                <span className="ml-2 text-amber-400">· Only the owner can amend or delete records.</span>
              )}
            </p>
          </div>
          <Button onClick={loadAll} variant="outline" size="sm" className="h-9"
            data-testid="stock-refresh-btn"
            title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Items tracked" value={summary?.items_total || 0} icon={Package} tone="sky"
            testid="stock-stat-items" />
          <StatCard label="Low stock" value={summary?.items_low || 0} icon={AlertTriangle}
            tone={summary?.items_low > 0 ? "red" : "slate"}
            testid="stock-stat-low" />
          <StatCard label="Pending requests" value={summary?.requests_pending || 0} icon={ShoppingCart}
            tone={summary?.requests_pending > 0 ? "amber" : "slate"}
            testid="stock-stat-pending" />
          <StatCard label="Today movements" value={summary?.today_movements || 0} icon={History} tone="slate"
            testid="stock-stat-moves" />
        </div>

        {/* Low-stock banner */}
        {summary?.low_items?.length > 0 && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm" data-testid="stock-low-banner">
            <div className="text-red-300 font-medium flex items-center gap-2 mb-1.5">
              <AlertTriangle className="w-4 h-4" />
              {summary.low_items.length} item{summary.low_items.length !== 1 ? "s" : ""} at or below low-stock threshold
            </div>
            <div className="flex flex-wrap gap-2">
              {summary.low_items.slice(0, 10).map((i) => (
                <span key={i.id} className="text-[11px] num px-2 py-1 rounded bg-slate-900/60 border border-slate-800">
                  {i.name} <span className="text-red-300">{i.balance}</span><span className="text-slate-500">/{i.threshold}</span> {i.unit}
                </span>
              ))}
              {summary.low_items.length > 10 && (
                <span className="text-[11px] text-slate-500 self-center">+{summary.low_items.length - 10} more</span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-800">
          <TabBtn active={tab === "items"} onClick={() => switchTab("items")} icon={Package} testid="stock-tab-items">
            Items
          </TabBtn>
          <TabBtn active={tab === "requests"} onClick={() => switchTab("requests")} icon={ShoppingCart} testid="stock-tab-requests">
            Requests
          </TabBtn>
          <TabBtn active={tab === "movements"} onClick={() => switchTab("movements")} icon={History} testid="stock-tab-movements"
            onActivate={async () => {
              const { data } = await api.get("/stock/items"); // refresh balance snapshot
              setItems(data);
              // Load most-recent movements across all items (server: get last 200 newest)
              const all = [];
              for (const it of data.slice(0, 30)) {
                const r = await api.get(`/stock/items/${it.id}/movements?limit=20`);
                all.push(...r.data);
              }
              all.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
              setMovements(all.slice(0, 200));
            }}>
            Movements
          </TabBtn>
        </div>

        {loading && <div className="text-center py-8 text-slate-500 text-sm">Loading…</div>}

        {/* ITEMS TAB */}
        {!loading && tab === "items" && (
          <ItemsTab
            items={filteredItems}
            categories={categories}
            q={q} setQ={setQ}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            isOwner={isOwner}
            onNew={() => setItemDlg({ mode: "new" })}
            onEdit={(item) => setItemDlg({ mode: "edit", item })}
            onDelete={deleteItem}
            onRequest={(item) => setRequestDlg({ item })}
            onReceive={(item) => setReceiveDlg({ item })}
            onAdjust={(item) => setAdjustDlg({ item })}
            onHistory={openHistory}
          />
        )}

        {/* REQUESTS TAB */}
        {!loading && tab === "requests" && (
          <RequestsTab
            requests={filteredRequests}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            items={items}
            isAdmin={isAdmin}
            isOwner={isOwner}
            currentUserId={user?.id}
            onNew={() => setRequestDlg({ item: null })}
            onApprove={approveRequest}
            onReject={rejectRequest}
            onIssue={(req) => setIssueDlg({ req })}
            onDelete={deleteRequest}
          />
        )}

        {/* MOVEMENTS TAB */}
        {!loading && tab === "movements" && (
          <MovementsTab movements={movements} />
        )}

        {/* Dialogs */}
        {itemDlg && (
          <ItemDialog
            mode={itemDlg.mode}
            item={itemDlg.item}
            categories={categories}
            onClose={() => setItemDlg(null)}
            onSave={(payload, id) => saveItem(payload, id)}
          />
        )}
        {requestDlg && (
          <RequestDialog
            item={requestDlg.item}
            items={items}
            onClose={() => setRequestDlg(null)}
            onSubmit={submitRequest}
          />
        )}
        {receiveDlg && (
          <ReceiveDialog
            item={receiveDlg.item}
            onClose={() => setReceiveDlg(null)}
            onSubmit={submitReceive}
          />
        )}
        {issueDlg && (
          <IssueDialog
            request={issueDlg.req}
            items={items}
            onClose={() => setIssueDlg(null)}
            onSubmit={issueRequest}
          />
        )}
        {adjustDlg && (
          <AdjustDialog
            item={adjustDlg.item}
            onClose={() => setAdjustDlg(null)}
            onSubmit={submitAdjust}
          />
        )}
        {historyItem && (
          <HistoryDialog
            item={historyItem}
            onClose={() => setHistoryItem(null)}
          />
        )}
      </div>
    </Layout>
  );
}

// ============================================================
// Sub-components
// ============================================================

function StatCard({ label, value, icon: Icon, tone = "slate", testid }) {
  const tones = {
    sky: "bg-sky-500/10 border-sky-500/30 text-sky-300",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    red: "bg-red-500/10 border-red-500/30 text-red-300",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    slate: "bg-slate-800/30 border-slate-800 text-slate-300",
  };
  return (
    <div className={`rounded-lg border hairline ${tones[tone] || tones.slate} p-3`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-70 num mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className="text-2xl font-semibold num">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, onActivate, icon: Icon, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={async () => { onClick(); if (onActivate) await onActivate(); }}
      className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 transition ${
        active
          ? "text-sky-300 border-sky-400"
          : "text-slate-400 border-transparent hover:text-slate-200"
      }`}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

// ---------- Items tab ----------
function ItemsTab({ items, categories, q, setQ, categoryFilter, setCategoryFilter,
  isOwner, onNew, onEdit, onDelete, onRequest, onReceive, onAdjust, onHistory }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name / SKU / category…"
            className="bg-[#0a0e1a] border-slate-800 h-9 pl-9"
            data-testid="stock-search-input"
          />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-[#0a0e1a] border border-slate-800 rounded h-9 text-sm px-2"
          data-testid="stock-cat-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <Button onClick={onNew} className="h-9 bg-sky-600 hover:bg-sky-500" data-testid="stock-new-item-btn">
          <Plus className="w-4 h-4 mr-1" /> New item
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">
          No items yet. Click <strong>New item</strong> to add bunk beds, uniforms, safety shoes, etc.
        </div>
      ) : (
        <div className="rounded-lg border hairline surface-1 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 num">
                <th className="text-left py-2 px-3">Item</th>
                <th className="text-left py-2 px-3 hidden md:table-cell">Category</th>
                <th className="text-right py-2 px-3">Balance</th>
                <th className="text-right py-2 px-3 hidden sm:table-cell">Low ≤</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const isLow = Number(i.balance) <= Number(i.low_stock_threshold);
                return (
                  <tr key={i.id} className="border-t border-slate-900 hover:bg-slate-800/30"
                      data-testid={`stock-item-row-${i.id}`}>
                    <td className="py-2 px-3">
                      <button onClick={() => onHistory(i)} className="text-left">
                        <div className="text-slate-100 hover:text-sky-300 transition">{i.name}</div>
                        {i.sku && <div className="text-[10px] text-slate-600 num">{i.sku}</div>}
                      </button>
                    </td>
                    <td className="py-2 px-3 hidden md:table-cell text-slate-400 text-xs">{i.category_name || "—"}</td>
                    <td className={`py-2 px-3 text-right num font-semibold ${isLow ? "text-red-300" : "text-slate-100"}`}>
                      {Number(i.balance).toLocaleString("en-US", { maximumFractionDigits: 2 })} <span className="text-[10px] text-slate-500">{i.unit}</span>
                    </td>
                    <td className="py-2 px-3 text-right num text-slate-500 hidden sm:table-cell">{i.low_stock_threshold}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <button onClick={() => onReceive(i)} title="Receive new stock (IN)"
                          className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-400"
                          data-testid={`stock-receive-${i.id}`}>
                          <ArrowDownToLine className="w-4 h-4" />
                        </button>
                        <button onClick={() => onRequest(i)} title="Request from this item"
                          className="p-1.5 rounded hover:bg-sky-500/20 text-sky-400"
                          data-testid={`stock-request-${i.id}`}>
                          <ShoppingCart className="w-4 h-4" />
                        </button>
                        {isOwner && (
                          <>
                            <button onClick={() => onAdjust(i)} title="Owner: manual balance adjust"
                              className="p-1.5 rounded hover:bg-amber-500/20 text-amber-400"
                              data-testid={`stock-adjust-${i.id}`}>
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button onClick={() => onEdit(i)} title="Owner: edit"
                              className="p-1.5 rounded hover:bg-slate-700 text-slate-300"
                              data-testid={`stock-edit-${i.id}`}>
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button onClick={() => onDelete(i)} title="Owner: delete"
                              className="p-1.5 rounded hover:bg-red-500/20 text-red-400"
                              data-testid={`stock-delete-${i.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------- Requests tab ----------
function RequestsTab({ requests, statusFilter, setStatusFilter, items, isAdmin, isOwner,
  currentUserId, onNew, onApprove, onReject, onIssue, onDelete }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 flex-wrap">
          {["", "Pending", "Approved", "Issued", "Rejected"].map((s) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)}
              data-testid={`stock-req-filter-${s || "all"}`}
              className={`px-2 py-1 rounded text-[11px] font-medium num transition ${
                statusFilter === s
                  ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                  : "bg-slate-800/40 text-slate-400 border border-slate-800 hover:text-slate-200"
              }`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button onClick={onNew} className="h-9 bg-sky-600 hover:bg-sky-500" data-testid="stock-new-request-btn">
          <Plus className="w-4 h-4 mr-1" /> New request
        </Button>
      </div>
      {requests.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">No requests in window.</div>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => {
            const isOwnRequest = r.requested_by === currentUserId;
            return (
              <div key={r.id} className="rounded-lg border hairline surface-1 p-3" data-testid={`stock-req-row-${r.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-100">{r.item_name}</span>
                      <span className="text-xs text-slate-500 num">
                        {Number(r.qty)} {r.item_unit}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border num ${REQUEST_STATUS_COLORS[r.status]}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 num mt-1 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full" style={{ background: r.requested_by_color || "#64748b" }} />
                        {r.requested_by_name}
                      </span>
                      <span className="text-slate-600">·</span>
                      <Clock className="w-3 h-3 inline" /> {(r.requested_at || "").slice(0, 16).replace("T", " ")}
                      {r.approver_name && (
                        <>
                          <span className="text-slate-600">·</span>
                          <span>approved by {r.approver_name}</span>
                        </>
                      )}
                      {r.issued_by_name && (
                        <>
                          <span className="text-slate-600">·</span>
                          <span>issued by {r.issued_by_name}</span>
                        </>
                      )}
                    </div>
                    {r.reason && <div className="text-xs text-slate-400 italic mt-1">"{r.reason}"</div>}
                    {r.approval_note && (
                      <div className={`text-[11px] mt-1 ${r.status === "Rejected" ? "text-red-300" : "text-blue-300"}`}>
                        Note: {r.approval_note}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isAdmin && r.status === "Pending" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => onApprove(r)}
                          className="h-8 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`stock-req-approve-${r.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onReject(r)}
                          className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10"
                          data-testid={`stock-req-reject-${r.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {isAdmin && (r.status === "Approved" || r.status === "Pending") && (
                      <Button size="sm" onClick={() => onIssue(r)}
                        className="h-8 bg-sky-600 hover:bg-sky-500"
                        data-testid={`stock-req-issue-${r.id}`}>
                        <ArrowUpFromLine className="w-3.5 h-3.5 mr-1" /> Issue
                      </Button>
                    )}
                    {isOwner && r.status !== "Issued" && (
                      <button onClick={() => onDelete(r)}
                        className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"
                        data-testid={`stock-req-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------- Movements tab ----------
function MovementsTab({ movements }) {
  if (movements.length === 0) {
    return <div className="text-center py-10 text-slate-500 text-sm">No movements yet. Use Receive / Request to start.</div>;
  }
  return (
    <div className="rounded-lg border hairline surface-1 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500 num">
            <th className="text-left py-2 px-3">When</th>
            <th className="text-left py-2 px-3">Item</th>
            <th className="text-left py-2 px-3 hidden sm:table-cell">Kind</th>
            <th className="text-right py-2 px-3">Delta</th>
            <th className="text-right py-2 px-3 hidden md:table-cell">After</th>
            <th className="text-left py-2 px-3 hidden md:table-cell">By</th>
            <th className="text-left py-2 px-3 hidden lg:table-cell">Note</th>
          </tr>
        </thead>
        <tbody>
          {movements.map((m) => (
            <tr key={m.id} className="border-t border-slate-900">
              <td className="py-2 px-3 text-[11px] num text-slate-500">{(m.timestamp || "").slice(0, 16).replace("T", " ")}</td>
              <td className="py-2 px-3 text-slate-100">{m.item_name}</td>
              <td className="py-2 px-3 hidden sm:table-cell text-xs text-slate-400 capitalize">{m.kind}</td>
              <td className={`py-2 px-3 text-right num font-semibold ${m.delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {m.delta >= 0 ? "+" : ""}{m.delta}
              </td>
              <td className="py-2 px-3 text-right num text-slate-300 hidden md:table-cell">{m.after_balance}</td>
              <td className="py-2 px-3 hidden md:table-cell text-xs text-slate-400">{m.by_user_name}</td>
              <td className="py-2 px-3 hidden lg:table-cell text-[11px] text-slate-500 italic truncate max-w-[280px]">{m.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Dialogs
// ============================================================

function ItemDialog({ mode, item, categories, onClose, onSave }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    sku: item?.sku || "",
    category_id: item?.category_id || "",
    unit: item?.unit || "pcs",
    low_stock_threshold: item?.low_stock_threshold ?? 5,
    notes: item?.notes || "",
  });
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    await onSave(form, mode === "edit" ? item.id : null);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100" data-testid="stock-item-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit item" : "New stock item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Item name *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Safety Shoes Size 9" data-testid="stock-item-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SKU (optional)">
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="SS-9" />
            </Field>
            <Field label="Unit">
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f1626] border-slate-800 text-slate-100">
                  {STOCK_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Category">
            <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0f1626] border-slate-800 text-slate-100">
                <SelectItem value="none">— Uncategorized —</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Low-stock threshold (alert when balance ≤ this)">
            <Input type="number" min={0} step="any" value={form.low_stock_threshold}
              onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
              data-testid="stock-item-threshold" />
          </Field>
          <Field label="Notes (optional)">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-sky-600 hover:bg-sky-500" data-testid="stock-item-save">
            {mode === "edit" ? "Save changes" : "Create item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ item, items, onClose, onSubmit }) {
  const [form, setForm] = useState({
    item_id: item?.id || "",
    qty: 1,
    reason: "",
  });
  const submit = () => {
    if (!form.item_id) { toast.error("Pick an item"); return; }
    if (!form.qty || Number(form.qty) <= 0) { toast.error("Quantity must be > 0"); return; }
    onSubmit(form);
  };
  const activeItem = items.find((i) => i.id === form.item_id) || item;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100" data-testid="stock-request-dialog">
        <DialogHeader>
          <DialogTitle>Request stock</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Item *">
            <Select value={form.item_id || ""} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-h-72">
                {items.filter((i) => i.is_active).map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name} <span className="text-[10px] text-slate-500">· {Number(i.balance)} {i.unit} in stock</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {activeItem && (
            <div className="text-[11px] text-slate-500 num">
              Current balance: <span className="text-slate-300">{Number(activeItem.balance)} {activeItem.unit}</span>
            </div>
          )}
          <Field label="Quantity *">
            <Input type="number" min={0} step="any" value={form.qty}
              onChange={(e) => setForm({ ...form, qty: e.target.value })}
              data-testid="stock-request-qty" />
          </Field>
          <Field label="Reason (optional)">
            <Textarea rows={3} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. New hire onboarding, replacement, hostel re-stock…" />
          </Field>
          <div className="text-[11px] text-slate-500">
            Your request goes to an admin for approval. You'll see the status here once they decide.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-sky-600 hover:bg-sky-500" data-testid="stock-request-submit">
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ item, onClose, onSubmit }) {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const submit = () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Quantity must be > 0"); return; }
    onSubmit({ item_id: item.id, qty: n, note });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100" data-testid="stock-receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive stock — {item.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-500 num">
            Current balance: <span className="text-slate-300">{Number(item.balance)} {item.unit}</span>
          </div>
          <Field label={`Quantity received (${item.unit}) *`}>
            <Input type="number" min={0} step="any" value={qty} onChange={(e) => setQty(e.target.value)}
              data-testid="stock-receive-qty" />
          </Field>
          <Field label="Note (e.g. supplier, invoice #, batch…)">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-500" data-testid="stock-receive-submit">
            <ArrowDownToLine className="w-4 h-4 mr-1" /> Add IN
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ request, items, onClose, onSubmit }) {
  const itm = items.find((i) => i.id === request.item_id);
  const [qty, setQty] = useState(request.qty);
  const [note, setNote] = useState("");
  const submit = () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Quantity must be > 0"); return; }
    if (itm && n > Number(itm.balance)) {
      if (!window.confirm(`Only ${itm.balance} ${itm.unit} in stock. Issue ${n} anyway? (will throw)`)) return;
    }
    onSubmit({ rid: request.id, qty: n, note });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100" data-testid="stock-issue-dialog">
        <DialogHeader>
          <DialogTitle>Issue stock to {request.requested_by_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-slate-800 p-2 text-[11px] num text-slate-400 bg-slate-900/40">
            Requested: <span className="text-slate-200">{request.qty} {request.item_unit}</span>{" "}
            of <span className="text-slate-200">{request.item_name}</span>
            <div className="text-slate-500">In stock: {itm ? `${itm.balance} ${itm.unit}` : "—"}</div>
          </div>
          <Field label={`Quantity to issue (${request.item_unit}) *`}>
            <Input type="number" min={0} step="any" value={qty} onChange={(e) => setQty(e.target.value)}
              data-testid="stock-issue-qty" />
          </Field>
          <Field label="Note (optional)">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-sky-600 hover:bg-sky-500" data-testid="stock-issue-submit">
            <ArrowUpFromLine className="w-4 h-4 mr-1" /> Issue + deduct
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ item, onClose, onSubmit }) {
  const [val, setVal] = useState(item.balance);
  const [note, setNote] = useState("");
  const submit = () => {
    if (Number(val) < 0) { toast.error("Balance cannot be negative"); return; }
    onSubmit({ item_id: item.id, new_balance: Number(val), note });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100" data-testid="stock-adjust-dialog">
        <DialogHeader>
          <DialogTitle>Manual balance adjust — {item.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-500 num">
            Current: <span className="text-slate-300">{Number(item.balance)} {item.unit}</span>
          </div>
          <Field label={`New balance (${item.unit}) *`}>
            <Input type="number" min={0} step="any" value={val} onChange={(e) => setVal(e.target.value)}
              data-testid="stock-adjust-value" />
          </Field>
          <Field label="Reason (recommended)">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Stocktake correction, damage, found extra…" />
          </Field>
          <div className="text-[11px] text-amber-400">
            ⚠ Owner-only — a delta movement will be written so the ledger stays balanced.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-amber-600 hover:bg-amber-500" data-testid="stock-adjust-submit">
            Apply adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ item, onClose }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get(`/stock/items/${item.id}/movements?limit=100`)
      .then(({ data }) => { if (alive) setMovements(data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [item.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="stock-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-sky-400" /> History — {item.name}
          </DialogTitle>
          <div className="text-[11px] text-slate-500 num">
            Current balance: {Number(item.balance)} {item.unit}
          </div>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {loading && <div className="text-center py-8 text-slate-500 text-sm">Loading…</div>}
          {!loading && movements.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">No movements yet.</div>
          )}
          <div className="space-y-2">
            {movements.map((m) => (
              <div key={m.id} className="rounded border border-slate-800 px-3 py-2 flex items-start gap-3">
                <div className={`mt-1 shrink-0 ${m.delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {m.delta >= 0 ? <ArrowDownToLine className="w-4 h-4" /> : <ArrowUpFromLine className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`font-semibold num ${m.delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {m.delta >= 0 ? "+" : ""}{m.delta} {item.unit}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider num text-slate-500">{m.kind}</span>
                    <span className="text-[10px] num text-slate-600">→ {m.after_balance}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 num">
                    {(m.timestamp || "").slice(0, 16).replace("T", " ")} · by {m.by_user_name}
                  </div>
                  {m.note && <div className="text-xs text-slate-400 italic mt-0.5">{m.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Helpers ----------
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 num">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
